import { useEffect, useState } from "react";
import {
  CircleNotch, Coins, Buildings, TrendUp, ChartLine, Receipt, ArrowRight,
  Wrench, Car,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { repApi, REP_TOKEN_KEY } from "@/lib/repApi";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DemoLaunchCard() {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");

  const startDemo = async (persona) => {
    setBusy(persona);
    setErr("");
    try {
      const token = localStorage.getItem(REP_TOKEN_KEY);
      if (!token) throw new Error("rep_token_missing");
      const r = await axios.post(
        `${API}/rep/start-demo`,
        { persona },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = r.data?.redirect_hint;
      if (!url) throw new Error("resposta sem redirect_hint");
      // Navegação na MESMA aba — sem popup, sem about:blank, sem
      // window.open. O rep vai pro simulador; volta pro portal via
      // botão Voltar do browser (histórico preservado).
      window.location.href = url;
    } catch (e) {
      console.error("[start-demo]", e);
      const msg = e?.response?.data?.detail?.reason
        || e?.response?.data?.detail
        || e?.message
        || "Falha ao iniciar demo";
      setErr(String(msg));
      setBusy(null);
    }
    // NOTA: não zeramos `busy` no sucesso porque a página inteira já
    // está saindo — o loading state fica visível até a navegação ocorrer.
  };

  return (
    <section
      data-testid="rep-demo-launcher"
      className="mb-7 relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-white/[0.14]"
    >
      {/* Ambient glow — sutil, substitui o antigo gradiente "AI slop" */}
      <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/[0.035] blur-3xl" />

      <div className="relative flex items-start justify-between gap-6 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
            </span>
            Ambiente de demonstração
          </div>
          <h2 className="font-heading text-xl font-semibold text-white mt-2 tracking-tight">
            Mostre o AgentUS em ação
          </h2>
          <p className="text-sm text-slate-400 mt-1.5 max-w-lg leading-relaxed">
            Escolha uma persona pré-carregada com clientes fictícios, produtos
            e histórico. Simule mensagens e demonstre a IA respondendo em
            tempo real na frente do seu prospect.
          </p>
        </div>

        <div className="flex gap-2 flex-wrap self-center">
          <button
            data-testid="start-demo-workshop"
            onClick={() => startDemo("workshop")}
            disabled={!!busy}
            className="group/btn inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full border border-white/[0.14] bg-white/[0.03] hover:bg-white/[0.08] hover:border-white/25 text-white text-[13px] font-medium tracking-tight transition-all disabled:opacity-40 disabled:pointer-events-none"
          >
            {busy === "workshop" ? (
              <CircleNotch size={14} weight="bold" className="animate-spin" />
            ) : (
              <Wrench size={14} weight="regular" className="text-slate-300 group-hover/btn:text-white transition-colors" />
            )}
            {busy === "workshop" ? "Iniciando…" : "Oficina"}
            <ArrowRight size={11} weight="bold" className="opacity-40 group-hover/btn:opacity-100 group-hover/btn:translate-x-0.5 transition-all" />
          </button>
          <button
            data-testid="start-demo-dealer"
            onClick={() => startDemo("dealer")}
            disabled={!!busy}
            className="group/btn inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-white text-black text-[13px] font-medium tracking-tight transition-all hover:bg-slate-100 disabled:opacity-40 disabled:pointer-events-none"
          >
            {busy === "dealer" ? (
              <CircleNotch size={14} weight="bold" className="animate-spin" />
            ) : (
              <Car size={14} weight="regular" />
            )}
            {busy === "dealer" ? "Iniciando…" : "Concessionária"}
            <ArrowRight size={11} weight="bold" className="opacity-70 group-hover/btn:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>

      {err && (
        <div
          data-testid="rep-demo-launcher-error"
          className="relative mt-4 text-[12px] text-rose-300/90 border border-rose-500/20 bg-rose-500/[0.06] rounded-lg px-3 py-2"
        >
          {err}
        </div>
      )}
    </section>
  );
}

function fmtBRL(cents) {
  return ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", maximumFractionDigits: 2,
  });
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

const STATUS_LABEL = {
  pending: "Em carência",
  liberable: "Liberada",
  paid: "Paga",
  cancelled: "Cancelada",
};

const STATUS_STYLE = {
  pending:   "bg-amber-500/10 text-amber-300",
  liberable: "bg-emerald-500/10 text-emerald-300",
  paid:      "bg-sky-500/10 text-sky-300",
  cancelled: "bg-slate-500/10 text-slate-400",
};

function Kpi({ icon: Icon, label, value, tone, testid }) {
  return (
    <div
      className="bg-white/[0.03] border border-white/10 rounded-lg p-5"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-slate-400 text-[11px] uppercase tracking-[0.18em]">
        <Icon size={12} weight="regular" /> {label}
      </div>
      <div className={`mt-2 font-heading text-2xl font-semibold tabular-nums ${tone || "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

export default function RepDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    repApi.get("/rep/dashboard").then(({ data }) => setData(data));
  }, []);

  if (!data) {
    return (
      <div className="px-4 md:px-8 py-10 md:py-12 text-center">
        <CircleNotch size={20} weight="regular" className="text-slate-500 animate-spin inline-block" />
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[1200px] mx-auto" data-testid="rep-dashboard">
      <header className="mb-7">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">
          Portal do Representante
        </div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1">
          Visão geral
        </h1>
        <p className="text-sm text-slate-400 mt-1.5">
          Clientes atribuídos a você, faturamento recorrente e comissões geradas
          pelos seus indicados.
        </p>
      </header>

      <DemoLaunchCard />

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          icon={Buildings}
          label="Clientes ativos"
          value={data.active_clients}
          tone="text-white"
          testid="kpi-active-clients"
        />
        <Kpi
          icon={Buildings}
          label="Total atribuídos"
          value={data.total_clients}
          tone="text-slate-300"
          testid="kpi-total-clients"
        />
        <Kpi
          icon={TrendUp}
          label="MRR carteira"
          value={fmtBRL(data.mrr_cents)}
          tone={data.mrr_cents > 0 ? "text-emerald-400" : "text-slate-500"}
          testid="kpi-mrr"
        />
        <Kpi
          icon={Coins}
          label="A receber (liberadas)"
          value={fmtBRL(data.commission_liberable_cents)}
          tone={data.commission_liberable_cents > 0 ? "text-emerald-400" : "text-slate-500"}
          testid="kpi-liberable"
        />
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <Kpi
          icon={ChartLine}
          label="Em carência"
          value={fmtBRL(data.commission_pending_cents)}
          tone="text-amber-300"
          testid="kpi-pending"
        />
        <Kpi
          icon={Receipt}
          label="Já recebidas"
          value={fmtBRL(data.commission_paid_cents)}
          tone="text-sky-300"
          testid="kpi-paid"
        />
        <Kpi
          icon={Receipt}
          label="Canceladas"
          value={fmtBRL(data.commission_cancelled_cents)}
          tone="text-slate-500"
          testid="kpi-cancelled"
        />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <div
          className="bg-white/[0.03] border border-white/10 rounded-lg overflow-hidden"
          data-testid="rep-recent-clients"
        >
          <header className="flex items-center justify-between px-5 py-3.5 border-b border-white/5">
            <h3 className="font-heading text-[13px] font-semibold tracking-tight">
              Clientes recentes
            </h3>
            <Link
              to="/representante/clientes"
              className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1"
            >
              ver todos <ArrowRight size={10} weight="regular" />
            </Link>
          </header>
          {data.recent_clients.length === 0 ? (
            <p className="px-5 py-10 text-center text-slate-500 text-sm">
              Nenhum cliente atribuído ainda. Compartilhe seu link de afiliado
              e os cadastros aparecerão aqui em tempo real.
            </p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {data.recent_clients.map((c) => (
                  <tr
                    key={c.tenant_id}
                    className="border-t border-white/5 first:border-t-0"
                    data-testid={`rep-recent-client-${c.tenant_id}`}
                  >
                    <td className="px-5 py-3">
                      <div className="text-white font-medium truncate max-w-[200px]">
                        {c.company_name}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {c.status === "active" ? "Ativo" : c.status}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {c.monthly_amount_cents > 0 ? (
                        <span className="text-emerald-400">
                          {fmtBRL(c.monthly_amount_cents)}
                        </span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                      <div className="text-[10px] text-slate-500">
                        {c.activated_at ? fmtDate(c.activated_at) : fmtDate(c.created_at)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div
          className="bg-white/[0.03] border border-white/10 rounded-lg overflow-hidden"
          data-testid="rep-recent-commissions"
        >
          <header className="flex items-center justify-between px-5 py-3.5 border-b border-white/5">
            <h3 className="font-heading text-[13px] font-semibold tracking-tight">
              Comissões recentes
            </h3>
            <Link
              to="/representante/comissoes"
              className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1"
            >
              ver todas <ArrowRight size={10} weight="regular" />
            </Link>
          </header>
          {data.recent_commissions.length === 0 ? (
            <p className="px-5 py-10 text-center text-slate-500 text-sm">
              Nenhuma comissão gerada ainda. As comissões aparecem automaticamente
              a cada pagamento de assinatura dos seus clientes.
            </p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {data.recent_commissions.map((c) => (
                  <tr
                    key={c.id}
                    className="border-t border-white/5 first:border-t-0"
                    data-testid={`rep-recent-commission-${c.id}`}
                  >
                    <td className="px-5 py-3">
                      <div className="text-white font-medium tabular-nums">
                        {fmtBRL(c.commission_cents)}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {fmtDate(c.paid_at)} · base {fmtBRL(c.amount_paid_cents)}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span
                        className={`text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-full ${STATUS_STYLE[c.status] || ""}`}
                      >
                        {STATUS_LABEL[c.status] || c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
